import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FiX, FiDelete } from 'react-icons/fi';
import { useClient } from '../contexts/ClientContext';
import { getCompanyFeatures } from '../api/authApi';

const BASE_WIDTH = 360;
const BASE_HEIGHT = 900; // Increased height to avoid scrolling in currency converter
const MIN_WIDTH = 220;
const MIN_HEIGHT = 320;

const interactiveTags = new Set(['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT', 'A', 'LABEL']);

function shouldBlockDrag(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.dataset.noDrag === 'true') return true;
  if (interactiveTags.has(target.tagName)) return true;
  return shouldBlockDrag(target.parentElement);
}

const SCROLLBAR_STYLE_ID = 'calculator-scrollbar-style';
let scrollStyleInjected = false;

type CalculatorMode = 'basic' | 'scientific' | 'converter';
type AngleMode = 'deg' | 'rad';

const STATIC_CURRENCIES = [
  // Major currencies
  { code: 'USD', name: 'United States Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan (RMB)', symbol: '¥' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'ILS', name: 'Israeli New Shekel', symbol: '₪' },
  
  // Asian currencies
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
  { code: 'TWD', name: 'Taiwan New Dollar', symbol: 'NT$' },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳' },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: '₨' },
  { code: 'MMK', name: 'Myanmar Kyat', symbol: 'K' },
  { code: 'KHR', name: 'Cambodian Riel', symbol: '៛' },
  { code: 'LAK', name: 'Lao Kip', symbol: '₭' },
  { code: 'MNT', name: 'Mongolian Tugrik', symbol: '₮' },
  { code: 'NPR', name: 'Nepalese Rupee', symbol: '₨' },
  { code: 'AFN', name: 'Afghan Afghani', symbol: '؋' },
  
  // Middle Eastern currencies
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'ر.س' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك' },
  { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع.' },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب' },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا' },
  { code: 'LBP', name: 'Lebanese Pound', symbol: '£' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: '£' },
  { code: 'IQD', name: 'Iraqi Dinar', symbol: 'ع.د' },
  { code: 'IRR', name: 'Iranian Rial', symbol: '﷼' },
  { code: 'YER', name: 'Yemeni Rial', symbol: '﷼' },
  { code: 'SYP', name: 'Syrian Pound', symbol: '£' },
  
  // European currencies
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'PLN', name: 'Polish Złoty', symbol: 'zł' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei' },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв' },
  { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn' },
  { code: 'RSD', name: 'Serbian Dinar', symbol: 'дин' },
  { code: 'BAM', name: 'Bosnia-Herzegovina Convertible Mark', symbol: 'КМ' },
  { code: 'MKD', name: 'Macedonian Denar', symbol: 'ден' },
  { code: 'ALL', name: 'Albanian Lek', symbol: 'L' },
  { code: 'ISK', name: 'Icelandic Króna', symbol: 'kr' },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴' },
  { code: 'BYN', name: 'Belarusian Ruble', symbol: 'Br' },
  { code: 'MDL', name: 'Moldovan Leu', symbol: 'L' },
  { code: 'GEL', name: 'Georgian Lari', symbol: '₾' },
  { code: 'AMD', name: 'Armenian Dram', symbol: '֏' },
  { code: 'AZN', name: 'Azerbaijani Manat', symbol: '₼' },
  
  // African currencies
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵' },
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh' },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'د.م.' },
  { code: 'DZD', name: 'Algerian Dinar', symbol: 'د.ج' },
  { code: 'TND', name: 'Tunisian Dinar', symbol: 'د.ت' },
  { code: 'XOF', name: 'West African CFA Franc', symbol: 'CFA' },
  { code: 'XAF', name: 'Central African CFA Franc', symbol: 'FCFA' },
  { code: 'AOA', name: 'Angolan Kwanza', symbol: 'Kz' },
  { code: 'MZN', name: 'Mozambican Metical', symbol: 'MT' },
  
  // North & Central American currencies
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$' },
  { code: 'GTQ', name: 'Guatemalan Quetzal', symbol: 'Q' },
  { code: 'CRC', name: 'Costa Rican Colón', symbol: '₡' },
  { code: 'HNL', name: 'Honduran Lempira', symbol: 'L' },
  { code: 'NIO', name: 'Nicaraguan Córdoba', symbol: 'C$' },
  { code: 'PAB', name: 'Panamanian Balboa', symbol: 'B/.' },
  { code: 'DOP', name: 'Dominican Peso', symbol: 'RD$' },
  { code: 'JMD', name: 'Jamaican Dollar', symbol: 'J$' },
  { code: 'TTD', name: 'Trinidad and Tobago Dollar', symbol: 'TT$' },
  { code: 'BBD', name: 'Barbadian Dollar', symbol: 'Bds$' },
  { code: 'BZD', name: 'Belize Dollar', symbol: 'BZ$' },
  { code: 'BMD', name: 'Bermudian Dollar', symbol: 'BD$' },
  { code: 'BSD', name: 'Bahamian Dollar', symbol: 'B$' },
  
  // South American currencies
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'ARS', name: 'Argentine Peso', symbol: '$' },
  { code: 'CLP', name: 'Chilean Peso', symbol: '$' },
  { code: 'COP', name: 'Colombian Peso', symbol: '$' },
  { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/' },
  { code: 'UYU', name: 'Uruguayan Peso', symbol: '$U' },
  { code: 'PYG', name: 'Paraguayan Guaraní', symbol: '₲' },
  { code: 'BOB', name: 'Bolivian Boliviano', symbol: 'Bs.' },
  { code: 'VES', name: 'Venezuelan Bolívar', symbol: 'Bs.S' },
  { code: 'GYD', name: 'Guyanese Dollar', symbol: 'G$' },
  { code: 'SRD', name: 'Surinamese Dollar', symbol: '$' },
  { code: 'FKP', name: 'Falkland Islands Pound', symbol: '£' },
  
  // Oceanian currencies
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'FJD', name: 'Fijian Dollar', symbol: 'FJ$' },
  { code: 'PGK', name: 'Papua New Guinean Kina', symbol: 'K' },
  { code: 'SBD', name: 'Solomon Islands Dollar', symbol: 'SI$' },
  { code: 'VUV', name: 'Vanuatu Vatu', symbol: 'Vt' },
  { code: 'WST', name: 'Samoan Tala', symbol: 'T' },
  { code: 'TOP', name: 'Tongan Paʻanga', symbol: 'T$' },
  
  // Other currencies
  { code: 'XPF', name: 'CFP Franc', symbol: '₣' },
  { code: 'XCD', name: 'East Caribbean Dollar', symbol: '$' },
];

const STATIC_CURRENCY_MAP = STATIC_CURRENCIES.reduce<Record<string, { code: string; name: string; symbol: string }>>(
  (acc, currency) => {
    acc[currency.code] = currency;
    return acc;
  },
  {}
);

function normalizeCurrencyMeta(
  code: string,
  catalogEntry?: { name: string; symbol: string; numericCode?: string | null; flag?: string | null; countries?: string[]; rate_USD?: number | null; rate_timestamp?: string }
): { code: string; name: string; symbol: string; flag?: string | null; countries?: string[] } {
  const fallback = STATIC_CURRENCY_MAP[code];
  const name = catalogEntry?.name || fallback?.name || code;
  const symbol = catalogEntry?.symbol || fallback?.symbol || code;
  return { 
    code, 
    name, 
    symbol,
    flag: catalogEntry?.flag || null,
    countries: catalogEntry?.countries || []
  };
}

function currencySortComparator(a: { code: string; name: string }, b: { code: string; name: string }) {
  return a.name.localeCompare(b.name || b.code);
}

const modeTabs: { key: CalculatorMode; label: string }[] = [
  { key: 'basic', label: 'בסיסי' },
  { key: 'scientific', label: 'מדעי' },
  { key: 'converter', label: 'ממיר מטבע' },
];

function formatResult(value: number, precision = 12) {
  if (!Number.isFinite(value)) return 'Error';
  const formatted = Number.parseFloat(Number(value).toPrecision(precision));
  if (!Number.isFinite(formatted)) return 'Error';
  const string = formatted.toString();
  return string;
}

const Calculator: React.FC = () => {
  const { client } = useClient();
  const [showCalculator, setShowCalculator] = useState(false);
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState<string | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [enabledFeatures, setEnabledFeatures] = useState<string[] | null>(null);
  const [featureChecked, setFeatureChecked] = useState(false);
  const [mode, setMode] = useState<CalculatorMode>('basic');
  const [angleMode, setAngleMode] = useState<AngleMode>('deg');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ offsetX: number; offsetY: number; pointerId: number } | null>(null);
  const resizeRef = useRef<{ 
    isResizing: boolean;
    startX: number; 
    startY: number; 
    startWidth: number; 
    startHeight: number; 
    startLeft: number;
    startTop: number;
    axis: 'both' | 'horizontal' | 'vertical';
  } | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scrollHideTimeout = useRef<number | null>(null);
  const [showScrollbars, setShowScrollbars] = useState(false);
  const [converterAmount, setConverterAmount] = useState('1');
  const [converterFrom, setConverterFrom] = useState('USD');
  const [converterTo, setConverterTo] = useState('ILS');
  const [currencyCatalog, setCurrencyCatalog] = useState<Record<string, { 
    name: string; 
    symbol: string; 
    numericCode?: string | null;
    flag?: string | null;
    countries?: string[];
    rate_USD?: number | null;
    rate_timestamp?: string;
  }>>({});
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [symbolsError, setSymbolsError] = useState<string | null>(null);
  const [conversionLoading, setConversionLoading] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const [conversionResult, setConversionResult] = useState<number | null>(null);
  const [conversionRate, setConversionRate] = useState<number | null>(null);
  const [conversionDate, setConversionDate] = useState<string | null>(null);

  const getInitialPosition = useCallback(() => {
    if (typeof window === 'undefined') return { x: 24, y: 24 };
    try {
      const raw = localStorage.getItem('calculator:position');
      if (!raw) return { x: 24, y: 24 };
      const parsed = JSON.parse(raw);
      if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
        return parsed;
      }
    } catch {}
    return { x: 24, y: 24 };
  }, []);

  const getInitialSize = useCallback(() => {
    if (typeof window === 'undefined') return { width: BASE_WIDTH, height: BASE_HEIGHT };
    try {
      const raw = localStorage.getItem('calculator:size');
      if (!raw) return { width: BASE_WIDTH, height: BASE_HEIGHT };
      const parsed = JSON.parse(raw);
      if (typeof parsed?.width === 'number' && typeof parsed?.height === 'number') {
        return {
          width: Math.max(parsed.width, MIN_WIDTH),
          height: Math.max(parsed.height, MIN_HEIGHT),
        };
      }
    } catch {}
    return { width: BASE_WIDTH, height: BASE_HEIGHT };
  }, []);

  const [position, setPosition] = useState<{ x: number; y: number }>(() => getInitialPosition());
  const [size, setSize] = useState<{ width: number; height: number }>(() => getInitialSize());
  
  // Use refs for smooth dragging without re-renders
  const positionRef = useRef(position);
  const sizeRef = useRef(size);
  const localStorageTimeoutRef = useRef<number | null>(null);
  
  // Update refs when state changes
  useEffect(() => {
    positionRef.current = position;
  }, [position]);
  
  useEffect(() => {
    sizeRef.current = size;
  }, [size]);

  // Debounced localStorage writes to prevent lag
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorageTimeoutRef.current) {
      clearTimeout(localStorageTimeoutRef.current);
    }
    localStorageTimeoutRef.current = window.setTimeout(() => {
      localStorage.setItem('calculator:position', JSON.stringify(position));
      localStorageTimeoutRef.current = null;
    }, 300);
    return () => {
      if (localStorageTimeoutRef.current) {
        clearTimeout(localStorageTimeoutRef.current);
      }
    };
  }, [position]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorageTimeoutRef.current) {
      clearTimeout(localStorageTimeoutRef.current);
    }
    localStorageTimeoutRef.current = window.setTimeout(() => {
      localStorage.setItem('calculator:size', JSON.stringify(size));
      localStorageTimeoutRef.current = null;
    }, 300);
    return () => {
      if (localStorageTimeoutRef.current) {
        clearTimeout(localStorageTimeoutRef.current);
      }
    };
  }, [size]);

  useEffect(() => {
    if (typeof window === 'undefined' || scrollStyleInjected) return;
    const style = document.createElement('style');
    style.id = SCROLLBAR_STYLE_ID;
    style.textContent = `
      .calculator-scroll {
        scrollbar-gutter: stable;
      }
      .calculator-scroll.hide-scroll {
        scrollbar-width: none;
      }
      .calculator-scroll.hide-scroll::-webkit-scrollbar {
        width: 0;
        height: 0;
      }
      .calculator-scroll.show-scroll {
        scrollbar-width: thin;
      }
      .calculator-scroll.show-scroll::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      .calculator-scroll.show-scroll::-webkit-scrollbar-track {
        background: transparent;
      }
      .calculator-scroll.show-scroll::-webkit-scrollbar-thumb {
        background-color: rgba(100, 116, 139, 0.55);
        border-radius: 9999px;
        border: 2px solid transparent;
        background-clip: padding-box;
      }
    `;
    document.head.appendChild(style);
    scrollStyleInjected = true;
  }, []);

  const clampPosition = useCallback(
    (x: number, y: number) => {
      if (typeof window === 'undefined') return { x, y };
      const maxX = Math.max(window.innerWidth - size.width, 0);
      const maxY = Math.max(window.innerHeight - size.height, 0);
      return {
        x: Math.min(Math.max(x, 0), maxX),
        y: Math.min(Math.max(y, 0), maxY),
      };
    },
    [size.width, size.height]
  );

  const clampSize = useCallback(
    (width: number, height: number) => {
      if (typeof window === 'undefined') return { width, height };
      const maxAllowedWidth = Math.max(window.innerWidth, MIN_WIDTH);
      const maxAllowedHeight = Math.max(window.innerHeight, MIN_HEIGHT);
      return {
        width: Math.min(Math.max(width, MIN_WIDTH), maxAllowedWidth),
        height: Math.min(Math.max(height, MIN_HEIGHT), maxAllowedHeight),
      };
    },
    []
  );

  // These callbacks are now handled in the mousemove effect for better performance

  const handleResizeMouseDown = useCallback(
    (event: React.MouseEvent, axis: 'both' | 'horizontal' | 'vertical' = 'both') => {
      event.preventDefault();
      event.stopPropagation();
      if (!containerRef.current) return;
      
      resizeRef.current = {
        isResizing: true,
        startX: event.clientX,
        startY: event.clientY,
        startWidth: size.width,
        startHeight: size.height,
        startLeft: position.x,
        startTop: position.y,
        axis,
      };
    },
    [size.width, size.height, position.x, position.y]
  );

  const showScrollbarsNow = useCallback(() => {
    if (!showScrollbars) {
      setShowScrollbars(true);
    }
    if (scrollHideTimeout.current) {
      window.clearTimeout(scrollHideTimeout.current);
      scrollHideTimeout.current = null;
    }
  }, [showScrollbars]);

  const hideScrollbarsLater = useCallback(() => {
    if (scrollHideTimeout.current) {
      window.clearTimeout(scrollHideTimeout.current);
    }
    scrollHideTimeout.current = window.setTimeout(() => {
      setShowScrollbars(false);
      scrollHideTimeout.current = null;
    }, 800);
  }, []);

  const handleScrollAreaEnter = useCallback(() => {
    showScrollbarsNow();
  }, [showScrollbarsNow]);

  const handleScrollAreaLeave = useCallback(() => {
    hideScrollbarsLater();
  }, [hideScrollbarsLater]);

  const handleScrollAreaScroll = useCallback(() => {
    showScrollbarsNow();
    hideScrollbarsLater();
  }, [showScrollbarsNow, hideScrollbarsLater]);

  const getCurrencyMeta = useCallback(
    (code: string) => {
      const catalogEntry = currencyCatalog[code];
      return normalizeCurrencyMeta(code, catalogEntry);
    },
    [currencyCatalog]
  );

  const currencyOptions = useMemo(() => {
    const map = new Map<string, { code: string; name: string; symbol: string; flag?: string | null; countries?: string[] }>();
    Object.keys(currencyCatalog).forEach((code) => {
      map.set(code, normalizeCurrencyMeta(code, currencyCatalog[code]));
    });
    STATIC_CURRENCIES.forEach((currency) => {
      if (!map.has(currency.code)) {
        map.set(currency.code, { ...currency, flag: null, countries: [] });
      }
    });
    return Array.from(map.values()).sort(currencySortComparator);
  }, [currencyCatalog]);

  const formattedConverted = useMemo(() => {
    if (conversionResult === null) return '';
    const meta = getCurrencyMeta(converterTo);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: meta.code,
        maximumFractionDigits: 6,
      }).format(conversionResult);
    } catch {
      return `${meta.symbol} ${formatResult(conversionResult, 10)}`;
    }
  }, [conversionResult, converterTo, getCurrencyMeta]);

  const singleRateDescription = useMemo(() => {
    if (conversionRate === null) return '';
    return `1 ${converterFrom} ≈ ${formatResult(conversionRate, 10)} ${converterTo}`;
  }, [conversionRate, converterFrom, converterTo]);
  const handleDragStart = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!containerRef.current) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (shouldBlockDrag(event.target)) return;
      event.preventDefault();
      const rect = containerRef.current.getBoundingClientRect();
      dragRef.current = {
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        pointerId: event.pointerId || 0,
      };
      // Drag handling is now done in the mousemove effect
    },
    []
  );


  useEffect(() => {
    let rafId: number | null = null;
    let lastUpdateTime = 0;
    const THROTTLE_MS = 16; // ~60fps
    
    const handleMouseMove = (e: MouseEvent) => {
      const now = performance.now();
      if (now - lastUpdateTime < THROTTLE_MS) {
        return; // Skip this frame
      }
      lastUpdateTime = now;
      
      // Cancel any pending animation frame
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      
      // Use requestAnimationFrame for smooth 60fps updates
      rafId = requestAnimationFrame(() => {
        if (dragRef.current && dragRef.current.pointerId !== undefined) {
          const { offsetX, offsetY } = dragRef.current;
          const { x, y } = clampPosition(e.clientX - offsetX, e.clientY - offsetY);
          
          // Only update if position actually changed
          if (positionRef.current.x !== x || positionRef.current.y !== y) {
            // Update DOM directly for smooth dragging without re-render
            if (containerRef.current) {
              containerRef.current.style.transform = `translate(${x}px, ${y}px)`;
            }
            positionRef.current = { x, y };
            // Defer state update to prevent lag
            setPosition({ x, y });
          }
        }

        if (resizeRef.current && resizeRef.current.isResizing) {
          const { startX, startY, startWidth, startHeight, axis } = resizeRef.current;
          const deltaX = e.clientX - startX;
          const deltaY = e.clientY - startY;

          let rawWidth = startWidth;
          let rawHeight = startHeight;

          if (axis === 'both' || axis === 'horizontal') {
            rawWidth = startWidth + deltaX;
          }
          if (axis === 'both' || axis === 'vertical') {
            rawHeight = startHeight + deltaY;
          }

          const { width: clampedWidth, height: clampedHeight } = clampSize(rawWidth, rawHeight);
          
          // Only update if size actually changed
          if (sizeRef.current.width !== clampedWidth || sizeRef.current.height !== clampedHeight) {
            // Update DOM directly for smooth resizing
            if (containerRef.current) {
              containerRef.current.style.width = `${clampedWidth}px`;
              containerRef.current.style.height = `${clampedHeight}px`;
            }
            sizeRef.current = { width: clampedWidth, height: clampedHeight };
            // Defer state update
            setSize({ width: clampedWidth, height: clampedHeight });
          }
        }
      });
    };

    const handleMouseUp = () => {
      // Sync final position/size to state
      if (dragRef.current) {
        setPosition(positionRef.current);
        dragRef.current = null;
      }
      
      if (resizeRef.current && resizeRef.current.isResizing) {
        setSize(sizeRef.current);
        resizeRef.current = null;
      }
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (scrollHideTimeout.current) {
        window.clearTimeout(scrollHideTimeout.current);
      }
    };
  }, [clampPosition, clampSize]);

  useEffect(() => {
    setPosition(prev => clampPosition(prev.x, prev.y));
  }, [size.width, size.height, clampPosition]);

  useEffect(() => {
    hideScrollbarsLater();
  }, [hideScrollbarsLater]);

  useEffect(() => {
    if (mode !== 'converter') return;
    if (Object.keys(currencyCatalog).length > 0) return;

    // Use only static currency data - no fetch
    const normalized: Record<string, { 
      name: string; 
      symbol: string; 
      numericCode?: string | null;
      flag?: string | null;
      countries?: string[];
      rate_USD?: number | null;
      rate_timestamp?: string;
    }> = {};

    STATIC_CURRENCIES.forEach((currency) => {
      normalized[currency.code] = {
        name: currency.name,
        symbol: currency.symbol,
        flag: null,
        countries: [],
        rate_USD: null,
        rate_timestamp: undefined,
      };
    });

    // Set USD rate to 1
    if (normalized.USD) {
      normalized.USD.rate_USD = 1;
      normalized.USD.rate_timestamp = new Date().toISOString();
    }

    setCurrencyCatalog(normalized);
    setSymbolsLoading(false);
    setSymbolsError(null);
  }, [mode, currencyCatalog]);

  useEffect(() => {
    if (mode !== 'converter') return;
    
    // Debounce to prevent flickering on rapid changes
    const timeoutId = setTimeout(() => {
      const amount = Number.parseFloat(converterAmount || '0');
      if (!Number.isFinite(amount)) {
        setConversionLoading(false);
        setConversionResult(null);
        setConversionRate(null);
        setConversionDate(null);
        setConversionError('סכום אינו תקין');
        return;
      }

      if (converterFrom === converterTo) {
        setConversionLoading(false);
        setConversionResult(amount);
        setConversionRate(1);
        setConversionDate(null);
        setConversionError(null);
        return;
      }

      let cancelled = false;
      setConversionLoading(true);
      setConversionError(null);

      const controller = new AbortController();

      (async () => {
      try {
        // Use exchangerate-api.com - reliable, free, no API key, supports CORS
        // This is a well-established service that works reliably
        
        const url = `https://api.exchangerate-api.com/v4/latest/${converterFrom}`;
        
        const res = await fetch(url, { 
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          signal: controller.signal,
          headers: {
            'Accept': 'application/json',
          }
        });
        if (cancelled) return;

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: לא ניתן לקבל שער המרה מהשרת`);
        }

        const data = await res.json();
        if (cancelled) return;

        if (!data || typeof data !== 'object' || !data.rates) {
          throw new Error('תגובה לא תקינה מהשרת');
        }

        if (!data.rates[converterTo]) {
          throw new Error(`לא נמצא שער המרה עבור ${converterTo}`);
        }

        const rate = Number(data.rates[converterTo]);
        if (!Number.isFinite(rate)) {
          throw new Error('שער המרה לא תקין');
        }

        const result = rate * amount;
        const date = data.date || new Date().toISOString().split('T')[0];

        setConversionResult(result);
        setConversionRate(rate);
        setConversionDate(date);
      } catch (err: any) {
        if (cancelled || err?.name === 'AbortError') return;
        
        // Provide user-friendly error messages
        let message = 'שגיאה בהמרת המטבע.';
        if (err?.message) {
          const errMsg = err.message;
          if (errMsg.includes('Failed to fetch') || errMsg.includes('NetworkError') || errMsg.includes('ERR_NAME_NOT_RESOLVED')) {
            message = 'שגיאת רשת - בדוק את החיבור לאינטרנט';
          } else if (errMsg.includes('CORS')) {
            message = 'שגיאת CORS - בעיה בהגדרות הדפדפן';
          } else {
            message = errMsg;
          }
        } else if (typeof err === 'string') {
          message = err;
        }
        
        setConversionError(message);
        setConversionResult(null);
        setConversionRate(null);
        setConversionDate(null);
      } finally {
        if (!cancelled) {
          setConversionLoading(false);
        }
      }
    })();

      return () => {
        cancelled = true;
        controller.abort();
      };
    }, 300); // 300ms debounce
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [mode, converterAmount, converterFrom, converterTo, currencyCatalog]);

  // Check if user has permission (admin, agent, manager)
  const hasPermission = ['admin', 'agent', 'manager'].includes(client?.role || '');

  // Get company from URL
  const getCompanyFromPath = () => {
    const path = window.location.pathname;
    const parts = path.split('/').filter(Boolean);
    if (parts[0] && parts[0] !== 'login' && parts[0] !== 'superadmin') {
      return parts[0];
    }
    return null;
  };

  // Load company features
  useEffect(() => {
    const company = getCompanyFromPath();
    if (!client) {
      return;
    }
    if (client?.role === 'superadmin' || !company || company === 'superadmin') {
      setEnabledFeatures(null); // null means allow all
      setFeatureChecked(true);
      return;
    }

    const fetchFeatures = async () => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      if (!headers) {
        setEnabledFeatures(null);
        setFeatureChecked(true);
        return;
      }

      try {
        const data = await getCompanyFeatures(company);
        const features = data.enabledFeatures || [];
        setEnabledFeatures(features);
      } catch (err: any) {
        // Silently handle timeout errors and 404/403 - don't log
        const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.message?.includes('exceeded');
        if (err?.response?.status !== 404 && err?.response?.status !== 403 && !isTimeout) {
          // Only log non-timeout, non-404, non-403 errors
          console.error('Failed to fetch company features:', err);
        }
        setEnabledFeatures(null);
      } finally {
        setFeatureChecked(true);
      }
    };

    fetchFeatures();
  }, [client?.role]);

  // Listen for feature updates
  useEffect(() => {
    const company = getCompanyFromPath();
    if (!client) {
      return;
    }
    if (client?.role === 'superadmin' || !company || company === 'superadmin') {
      return;
    }

    const handleFeatureUpdate = () => {
      const fetchFeatures = async () => {
        const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

        if (!headers) {
          setEnabledFeatures(null);
          setFeatureChecked(true);
          return;
        }

        try {
          const data = await getCompanyFeatures(company);
          const features = data.enabledFeatures || [];
          setEnabledFeatures(features);
        } catch (err: any) {
          console.error('Failed to fetch company features:', err);
          setEnabledFeatures(null);
        } finally {
          setFeatureChecked(true);
        }
      };
      fetchFeatures();
    };

    window.addEventListener('company-features-updated', handleFeatureUpdate as EventListener);
    return () => {
      window.removeEventListener('company-features-updated', handleFeatureUpdate as EventListener);
    };
  }, [client?.role]);

  useEffect(() => {
    if (!hasPermission) {
      return;
    }

    // Check if calculator feature is enabled
    // If enabledFeatures is null, allow (not loaded yet or superadmin)
    // If enabledFeatures is empty array [], no features enabled
    // If enabledFeatures has items, check if calculator is in the list
    if (enabledFeatures !== null && enabledFeatures.length > 0) {
      if (!enabledFeatures.includes('calculator')) {
        // Feature not enabled - don't listen to events
        return;
      }
    }

    // Listen for sidebar click to toggle calculator
    const handleToggle = (e: Event) => {
      setShowCalculator((prev) => !prev);
    };

    window.addEventListener('calculator', handleToggle as EventListener);
    return () => {
      window.removeEventListener('calculator', handleToggle as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, enabledFeatures, featureChecked]);

  // Keyboard support
  useEffect(() => {
    if (!showCalculator) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const isEditable =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        (target && (target as HTMLElement).isContentEditable);

      if (isEditable || e.ctrlKey || e.metaKey) {
        return;
      }

      // Prevent default behavior for calculator keys when not typing in inputs
      if (/^[0-9.+\-*/=%]$/.test(e.key) || e.key === 'Enter' || e.key === 'Escape' || e.key === 'Backspace') {
        e.preventDefault();
      }

      // Numbers
      if (/^[0-9]$/.test(e.key)) {
        inputDigit(e.key);
      }
      // Decimal
      else if (e.key === '.') {
        inputDecimal();
      }
      // Operations
      else if (e.key === '+') {
        performOperation('+');
      } else if (e.key === '-') {
        performOperation('-');
      } else if (e.key === '*' || e.key === '×') {
        performOperation('×');
      } else if (e.key === '/' || e.key === '÷') {
        performOperation('÷');
      } else if (e.key === '%') {
        percentage();
      }
      // Equals
      else if (!e.shiftKey && (e.key === 'Enter' || e.key === '=')) {
        handleEquals();
      }
      // Clear
      else if (e.key === 'Escape') {
        clear();
      }
      // Backspace
      else if (e.key === 'Backspace') {
        backspace();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [showCalculator, display, previousValue, operation, waitingForOperand]);

  const inputDigit = (digit: string) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const performOperation = (nextOperation: string) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(display);
    } else if (operation) {
      const currentValue = previousValue || '0';
      const currentValueFloat = parseFloat(currentValue);
      const newValue = calculate(currentValueFloat, inputValue, operation);

      setDisplay(String(newValue));
      setPreviousValue(String(newValue));
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const calculate = (firstValue: number, secondValue: number, operation: string): number => {
    switch (operation) {
      case '+':
        return firstValue + secondValue;
      case '-':
        return firstValue - secondValue;
      case '×':
        return firstValue * secondValue;
      case '÷':
        return secondValue !== 0 ? firstValue / secondValue : 0;
      case '%':
        return firstValue % secondValue;
      case '^':
        return Math.pow(firstValue, secondValue);
      default:
        return secondValue;
    }
  };

  const handleEquals = () => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operation) {
      const currentValue = parseFloat(previousValue);
      const newValue = calculate(currentValue, inputValue, operation);

      setDisplay(String(newValue));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(false);
    }
  };

  const toggleSign = () => {
    const value = parseFloat(display);
    setDisplay(String(value * -1));
  };

  const percentage = () => {
    const value = parseFloat(display);
    setDisplay(String(value / 100));
  };

  const backspace = () => {
    if (display.length > 1) {
      setDisplay(display.slice(0, -1));
    } else {
      setDisplay('0');
    }
  };

  const handlePadPress = (label: string) => {
    if (/^[0-9]$/.test(label)) {
      inputDigit(label);
      return;
    }

    switch (label) {
      case 'AC':
        clear();
        break;
      case '⌫':
        backspace();
        break;
      case '+/-':
        toggleSign();
        break;
      case '.':
        inputDecimal();
        break;
      case '%':
        percentage();
        break;
      case '÷':
      case '×':
      case '-':
      case '+':
        performOperation(label);
        break;
      case '^':
        performOperation('^');
        break;
      default:
        break;
    }
  };

  const showError = () => {
    setDisplay('Error');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(true);
  };

  const applyUnary = (fn: (value: number) => number, validator?: (value: number) => boolean) => {
    const value = parseFloat(display);
    if (Number.isNaN(value)) return;
    if (validator && !validator(value)) {
      showError();
      return;
    }
    const result = fn(value);
    if (!Number.isFinite(result)) {
      showError();
      return;
    }
    setDisplay(formatResult(result));
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(true);
  };

  const applyTrig = (kind: 'sin' | 'cos' | 'tan') => {
    const value = parseFloat(display);
    if (Number.isNaN(value)) return;
    const radians = angleMode === 'deg' ? (value * Math.PI) / 180 : value;
    let result: number;
    switch (kind) {
      case 'sin':
        result = Math.sin(radians);
        break;
      case 'cos':
        result = Math.cos(radians);
        break;
      case 'tan':
        if (Math.abs(Math.cos(radians)) < 1e-12) {
          showError();
          return;
        }
        result = Math.tan(radians);
        break;
      default:
        result = value;
    }
    if (Math.abs(result) < 1e-12) result = 0;
    applyUnary(() => result);
  };

  const insertConstant = (constant: number) => {
    setDisplay(formatResult(constant));
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(true);
  };

  const swapCurrencies = () => {
    setConverterFrom(converterTo);
    setConverterTo(converterFrom);
  };

  const handleAmountChange = (value: string) => {
    const cleaned = value.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
    const parts = cleaned.split('.');
    const normalized = parts.length > 1 ? `${parts.shift()}.${parts.join('')}` : cleaned;
    setConverterAmount(normalized);
  };

  const factorial = (n: number) => {
    let result = 1;
    for (let i = 2; i <= n; i += 1) {
      result *= i;
    }
    return result;
  };

  const handleFactorial = () => {
    const value = parseFloat(display);
    if (!Number.isInteger(value) || value < 0 || value > 170) {
      showError();
      return;
    }
    const result = factorial(value);
    if (!Number.isFinite(result)) {
      showError();
      return;
    }
    setDisplay(formatResult(result));
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(true);
  };

  if (!hasPermission) {
    return null;
  }
  
  if (!showCalculator) {
    return null;
  }

  // Check if calculator feature is enabled
  // If enabledFeatures is null, allow (not loaded yet or superadmin)
  // If enabledFeatures is empty array [], no features enabled
  // If enabledFeatures has items, check if calculator is in the list
  if (featureChecked && enabledFeatures !== null && enabledFeatures.length > 0) {
    if (!enabledFeatures.includes('calculator')) {
      return null;
    }
  }

  // Memoized button component to prevent unnecessary re-renders
  const CalcButton = React.memo<{ 
    label: string; 
    variant?: 'primary' | 'accent' | 'danger' | 'ghost'; 
    span?: number;
    onClick: () => void;
  }>(({ label, variant = 'primary', span = 1, onClick }) => {
    const base =
      'h-12 sm:h-14 rounded-2xl font-semibold text-base flex items-center justify-center transition-transform duration-150 select-none active:scale-[0.96] shadow-lg hover:shadow-xl backdrop-blur-sm border border-white/20 relative overflow-hidden group will-change-transform';
    const variants: Record<string, string> = {
      primary: 'bg-gradient-to-br from-slate-700/80 via-slate-800/80 to-slate-700/80 hover:from-slate-600/90 hover:via-slate-700/90 hover:to-slate-600/90 text-slate-50 hover:border-white/30',
      accent: 'bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500 hover:from-violet-400 hover:via-purple-400 hover:to-fuchsia-400 text-white shadow-purple-500/30 hover:shadow-purple-400/40 border-purple-400/30',
      danger: 'bg-gradient-to-br from-rose-500 via-pink-500 to-red-500 hover:from-rose-400 hover:via-pink-400 hover:to-red-400 text-white shadow-rose-500/30 hover:shadow-rose-400/40 border-rose-400/30',
      ghost: 'bg-white/5 hover:bg-white/10 text-slate-200 hover:text-white border-white/10 hover:border-white/20',
    };
    const spanClass = span === 2 ? 'col-span-2' : span === 3 ? 'col-span-3' : '';
    return (
      <button
        onClick={onClick}
        type="button"
        data-no-drag="true"
        className={`${base} ${variants[variant]} ${spanClass}`}
      >
        <span className="relative z-10">{label === '⌫' ? <FiDelete size={22} /> : label}</span>
        <span className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/0 to-white/0 group-hover:from-white/10 group-hover:via-white/5 group-hover:to-white/0 transition-opacity duration-200"></span>
      </button>
    );
  }, (prev, next) => prev.label === next.label && prev.variant === next.variant && prev.span === next.span);

  CalcButton.displayName = 'CalcButton';

  const renderButton = (label: string, config: { variant?: 'primary' | 'accent' | 'danger' | 'ghost'; span?: number } = {}) => {
    const { variant = 'primary', span = 1 } = config;
    return (
      <CalcButton
        key={label}
        label={label}
        variant={variant}
        span={span}
        onClick={() => handlePadPress(label)}
      />
    );
  };

  // Memoized function button component
  const FunctionButton = React.memo<{
    label: React.ReactNode;
    onClick: () => void;
    variant?: 'primary' | 'accent' | 'danger' | 'ghost';
  }>(({ label, onClick, variant = 'ghost' }) => {
    const base =
      'h-12 sm:h-14 rounded-2xl font-semibold text-sm sm:text-base flex items-center justify-center transition-transform duration-150 select-none active:scale-[0.96] shadow-lg hover:shadow-xl backdrop-blur-sm border border-white/20 relative overflow-hidden group will-change-transform';
    const variants: Record<string, string> = {
      primary: 'bg-gradient-to-br from-slate-700/80 via-slate-800/80 to-slate-700/80 hover:from-slate-600/90 hover:via-slate-700/90 hover:to-slate-600/90 text-slate-50 hover:border-white/30',
      accent: 'bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500 hover:from-violet-400 hover:via-purple-400 hover:to-fuchsia-400 text-white shadow-purple-500/30 hover:shadow-purple-400/40 border-purple-400/30',
      danger: 'bg-gradient-to-br from-rose-500 via-pink-500 to-red-500 hover:from-rose-400 hover:via-pink-400 hover:to-red-400 text-white shadow-rose-500/30 hover:shadow-rose-400/40 border-rose-400/30',
      ghost: 'bg-white/5 hover:bg-white/10 text-slate-200 hover:text-white border-white/10 hover:border-white/20',
    };
    return (
      <button
        type="button"
        data-no-drag="true"
        onClick={onClick}
        className={`${base} ${variants[variant]}`}
      >
        <span className="relative z-10">{label}</span>
        <span className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/0 to-white/0 group-hover:from-white/10 group-hover:via-white/5 group-hover:to-white/0 transition-opacity duration-200"></span>
      </button>
    );
  }, (prev, next) => {
    const prevLabel = typeof prev.label === 'string' ? prev.label : String(prev.label);
    const nextLabel = typeof next.label === 'string' ? next.label : String(next.label);
    return prevLabel === nextLabel && prev.variant === next.variant;
  });

  FunctionButton.displayName = 'FunctionButton';

  const renderFunctionButton = (
    label: React.ReactNode,
    onClick: () => void,
    config: { variant?: 'primary' | 'accent' | 'danger' | 'ghost' } = {}
  ) => {
    const { variant = 'ghost' } = config;
    const key = typeof label === 'string' ? label : String(label);
    return (
      <FunctionButton
        key={key}
        label={label}
        variant={variant}
        onClick={onClick}
      />
    );
  };

  const renderModeSwitcher = () => (
    <div className="bg-gradient-to-r from-slate-800/80 via-slate-900/80 to-slate-800/80 backdrop-blur-xl border border-white/20 rounded-3xl p-1.5 flex items-center gap-1.5 text-sm sm:text-base font-bold shadow-inner shadow-black/30">
      {modeTabs.map((tab) => {
        const active = mode === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            className={`flex-1 px-5 py-3 rounded-2xl transition-all duration-200 relative overflow-hidden ${
              active
                ? 'bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 text-white shadow-lg shadow-purple-500/50 scale-[1.02] font-extrabold'
                : 'bg-slate-700/40 text-slate-100 hover:text-white hover:bg-slate-600/50 hover:scale-[1.01] border border-slate-600/30 font-semibold'
            }`}
            onClick={() => setMode(tab.key)}
            data-no-drag="true"
          >
            <span className="relative z-10 drop-shadow-sm">{tab.label}</span>
            {active && (
              <span className="absolute inset-0 bg-gradient-to-r from-white/25 via-transparent to-white/25"></span>
            )}
          </button>
        );
      })}
    </div>
  );

  const renderDisplay = () => (
    <div className="relative bg-gradient-to-br from-slate-900/90 via-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-white/20 rounded-3xl px-6 py-7 shadow-[inset_0_8px_32px_-12px_rgba(0,0,0,0.5),0_4px_16px_rgba(0,0,0,0.3)] overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none"></div>
      {operation && previousValue && (
        <div className="text-slate-400 text-sm mb-3 tracking-widest flex justify-end items-center gap-2 relative z-10 font-medium">
          <span className="opacity-80">{previousValue}</span>
          <span className="text-purple-400 font-bold">{operation}</span>
        </div>
      )}
      <div className="text-right text-5xl sm:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-slate-50 via-white to-slate-200 tracking-tight leading-none break-words relative z-10 drop-shadow-lg">
        {display}
      </div>
    </div>
  );

  const renderScientificPanel = () => (
    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
      {renderFunctionButton(
        angleMode === 'deg' ? 'DEG' : 'RAD',
        () => setAngleMode((prev) => (prev === 'deg' ? 'rad' : 'deg')),
        { variant: 'accent' }
      )}
      {renderFunctionButton('sin', () => applyTrig('sin'))}
      {renderFunctionButton('cos', () => applyTrig('cos'))}
      {renderFunctionButton('tan', () => applyTrig('tan'))}
      {renderFunctionButton('π', () => insertConstant(Math.PI))}
      {renderFunctionButton('e', () => insertConstant(Math.E))}
      {renderFunctionButton('ln', () => applyUnary((value) => Math.log(value), (value) => value > 0))}
      {renderFunctionButton('log', () => applyUnary((value) => Math.log10(value), (value) => value > 0))}
      {renderFunctionButton('exp', () => applyUnary((value) => Math.exp(value)))}
      {renderFunctionButton('√', () => applyUnary((value) => Math.sqrt(value), (value) => value >= 0))}
      {renderFunctionButton('x²', () => applyUnary((value) => value ** 2))}
      {renderFunctionButton('x³', () => applyUnary((value) => value ** 3))}
      {renderFunctionButton('xʸ', () => performOperation('^'), { variant: 'primary' })}
      {renderFunctionButton('1/x', () => applyUnary((value) => 1 / value, (value) => value !== 0))}
      {renderFunctionButton('|x|', () => applyUnary((value) => Math.abs(value)))}
      {renderFunctionButton('n!', handleFactorial)}
      {renderFunctionButton('10ˣ', () => applyUnary((value) => Math.pow(10, value)))}
    </div>
  );

  const renderBasicPad = () => (
    <div className="grid grid-cols-4 gap-2 flex-1 pb-2">
      <button
        onClick={clear}
        type="button"
        data-no-drag="true"
        className="col-span-2 h-12 sm:h-14 bg-gradient-to-br from-rose-500 via-pink-500 to-red-500 hover:from-rose-400 hover:via-pink-400 hover:to-red-400 text-white font-semibold text-base rounded-2xl transition-all duration-200 shadow-lg hover:shadow-xl shadow-rose-500/30 hover:shadow-rose-400/40 border border-rose-400/30 active:scale-[0.96] backdrop-blur-sm relative overflow-hidden group"
      >
        <span className="relative z-10">AC</span>
        <span className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/0 to-white/0 group-hover:from-white/20 group-hover:via-white/10 group-hover:to-white/20 transition-all duration-300"></span>
      </button>
      {renderButton('⌫', { variant: 'danger' })}
      {renderButton('÷', { variant: 'accent' })}

      {['7', '8', '9'].map((label) => renderButton(label, { variant: 'primary' }))}
      {renderButton('×', { variant: 'accent' })}

      {['4', '5', '6'].map((label) => renderButton(label, { variant: 'primary' }))}
      {renderButton('-', { variant: 'accent' })}

      {['1', '2', '3'].map((label) => renderButton(label, { variant: 'primary' }))}
      <button
        onClick={handleEquals}
        type="button"
        data-no-drag="true"
        className="row-span-2 bg-gradient-to-br from-emerald-500 via-teal-500 to-green-500 hover:from-emerald-400 hover:via-teal-400 hover:to-green-400 text-white font-semibold text-base rounded-2xl transition-all duration-200 shadow-lg hover:shadow-xl shadow-emerald-500/30 hover:shadow-emerald-400/40 border border-emerald-400/30 active:scale-[0.96] flex items-center justify-center backdrop-blur-sm relative overflow-hidden group"
      >
        <span className="relative z-10">=</span>
        <span className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/0 to-white/0 group-hover:from-white/20 group-hover:via-white/10 group-hover:to-white/20 transition-all duration-300"></span>
      </button>

      {renderButton('+/-', { variant: 'primary' })}
      {renderButton('0', { variant: 'primary' })}
      {renderButton('.', { variant: 'primary' })}
      {renderButton('+', { variant: 'accent' })}

      {renderButton('%', { variant: 'ghost' })}
      {renderFunctionButton('√', () => applyUnary((value) => Math.sqrt(value), (value) => value >= 0))}
      {renderFunctionButton('^', () => performOperation('^'), { variant: 'ghost' })}
      {renderFunctionButton('1/x', () => applyUnary((value) => 1 / value, (value) => value !== 0))}
    </div>
  );

  const renderConverter = () => {
    const fromMeta = getCurrencyMeta(converterFrom);
    const toMeta = getCurrencyMeta(converterTo);

    return (
      <div className="flex flex-col gap-4 h-full">
        {symbolsLoading && (
          <div className="rounded-2xl border border-violet-400/40 bg-gradient-to-r from-violet-500/20 to-purple-500/20 backdrop-blur-sm text-violet-100 px-5 py-3.5 text-sm font-medium shadow-lg shadow-violet-500/10">
            טוען רשימת מטבעות...
          </div>
        )}
        {symbolsError && (
          <div className="rounded-2xl border border-rose-400/40 bg-gradient-to-r from-rose-500/20 to-pink-500/20 backdrop-blur-sm text-rose-100 px-5 py-3.5 text-sm font-medium shadow-lg shadow-rose-500/10">
            {symbolsError}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
          <div className="flex flex-col gap-2">
            <label className="text-xs text-slate-300 font-semibold">סכום</label>
            <input
              type="text"
              inputMode="decimal"
              value={converterAmount}
              onChange={(e) => handleAmountChange(e.target.value)}
              className="w-full px-5 py-3 rounded-2xl bg-white/95 text-gray-900 border border-white/70 focus:border-violet-400 focus:ring-2 focus:ring-violet-300/50 outline-none text-sm font-semibold transition-all duration-200 shadow-md shadow-black/10 hover:shadow-lg hover:shadow-black/20"
              data-no-drag="true"
            />
            <select
              className="w-full px-4 py-2.5 rounded-2xl bg-white/90 text-gray-900 border border-white/60 focus:border-violet-400 focus:ring-2 focus:ring-violet-300/50 outline-none text-sm font-semibold transition-all duration-200 shadow-md shadow-black/10 hover:shadow-lg hover:shadow-black/20 cursor-pointer"
              value={converterFrom}
              onChange={(e) => setConverterFrom(e.target.value)}
              data-no-drag="true"
            >
              {currencyOptions.map((currency) => (
                <option key={`from-${currency.code}`} value={currency.code}>
                  {currency.flag ? `${currency.flag} ` : ''}{currency.symbol} {currency.code} — {currency.name}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={swapCurrencies}
            data-no-drag="true"
            className="h-12 sm:h-14 px-5 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 hover:from-white/20 hover:to-white/10 text-slate-200 hover:text-white border border-white/20 hover:border-white/30 backdrop-blur-xl transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl shadow-black/20 active:scale-95"
            title="החלף בין המטבעות"
          >
            ⇄
          </button>

          <div className="flex flex-col gap-2">
            <label className="text-xs text-slate-300 font-semibold">תוצאה</label>
            <div className="w-full px-5 py-3 rounded-2xl bg-gradient-to-br from-slate-900/80 via-slate-800/80 to-slate-900/80 backdrop-blur-xl text-slate-100 border border-white/20 text-lg font-bold min-h-[52px] flex items-center justify-between gap-3 shadow-inner shadow-black/20">
              <span>
                {conversionLoading
                  ? 'מחשב...'
                  : conversionError
                    ? '—'
                    : formattedConverted || '—'}
              </span>
              <span className="text-sm text-slate-400 font-medium">{toMeta.symbol}</span>
            </div>
            <select
              className="w-full px-4 py-2.5 rounded-2xl bg-white/90 text-gray-900 border border-white/60 focus:border-violet-400 focus:ring-2 focus:ring-violet-300/50 outline-none text-sm font-semibold transition-all duration-200 shadow-md shadow-black/10 hover:shadow-lg hover:shadow-black/20 cursor-pointer"
              value={converterTo}
              onChange={(e) => setConverterTo(e.target.value)}
              data-no-drag="true"
            >
              {currencyOptions.map((currency) => (
                <option key={`to-${currency.code}`} value={currency.code}>
                  {currency.flag ? `${currency.flag} ` : ''}{currency.symbol} {currency.code} — {currency.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {conversionError && (
          <div className="rounded-2xl border border-rose-400/50 bg-gradient-to-r from-rose-500/15 to-pink-500/15 backdrop-blur-sm text-rose-100 px-5 py-3.5 text-sm font-medium shadow-lg shadow-rose-500/10">
            {conversionError}
          </div>
        )}

        {!conversionError && conversionResult !== null && (
          <div className="rounded-2xl border border-white/20 bg-gradient-to-br from-white/10 via-white/5 to-white/10 backdrop-blur-xl p-5 text-sm text-slate-200 space-y-2 shadow-lg shadow-black/20">
            <div>
              {converterAmount || '0'} {fromMeta.symbol} {fromMeta.code} → {formattedConverted || `${toMeta.symbol} ${formatResult(conversionResult, 10)}`}
            </div>
            {singleRateDescription && <div>{singleRateDescription}</div>}
            {conversionDate ? (() => {
              try {
                const date = new Date(conversionDate);
                const now = new Date();
                // Always use today's date if the date is in the future
                const displayDate = date > now ? now : date;
                const isValidDate = !isNaN(date.getTime());
                
                if (isValidDate) {
                  return (
                    <div className="text-xs text-slate-400 space-y-1">
                      <div>
                        תאריך עדכון: {displayDate.toLocaleDateString('he-IL', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit'
                        })}
                      </div>
                      <div className="text-amber-300/80 font-semibold">
                        נא לבדוק את שערי המטבע כדי להבטיח דיוק מירבי
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="text-xs text-slate-400 space-y-1">
                    <div className="text-amber-300/80 font-semibold">
                      נא לבדוק את שערי המטבע כדי להבטיח דיוק מירבי
                    </div>
                  </div>
                );
              } catch {
                return (
                  <div className="text-xs text-slate-400">
                    <div className="text-amber-300/80 font-semibold">
                      נא לבדוק את שערי המטבע כדי להבטיח דיוק מירבי.
                    </div>
                  </div>
                );
              }
            })() : (
              <div className="text-xs text-slate-400">
                <div className="text-amber-300/80 font-semibold">
                  נא לבדוק את שערי המטבע כדי להבטיח דיוק מירבי.
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  return (
    <div
      ref={containerRef}
      className="fixed z-[2000] select-none drop-shadow-2xl will-change-transform"
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        transform: 'translateZ(0)', // GPU acceleration
      }}
    >
      <div className="relative h-full w-full rounded-[32px] overflow-hidden bg-gradient-to-br from-slate-950/95 via-slate-900/95 to-slate-950/95 backdrop-blur-2xl border border-white/20 shadow-[0_40px_70px_-35px_rgba(0,0,0,0.8),0_0_0_1px_rgba(255,255,255,0.1)] before:absolute before:inset-0 before:bg-gradient-to-br before:from-purple-500/5 before:via-transparent before:to-blue-500/5 before:pointer-events-none will-change-transform">
        <div
          ref={scrollRef}
          className={`absolute inset-0 overflow-auto calculator-scroll ${showScrollbars ? 'show-scroll' : 'hide-scroll'}`}
          onMouseEnter={handleScrollAreaEnter}
          onMouseLeave={handleScrollAreaLeave}
          onScroll={handleScrollAreaScroll}
        >
          <div
            className="flex h-full w-full flex-col"
            style={{ minWidth: BASE_WIDTH, minHeight: BASE_HEIGHT }}
          >
            <div
              className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-900/98 via-slate-800/95 to-slate-900/98 backdrop-blur-xl border-b border-white/20 text-slate-100 cursor-grab active:cursor-grabbing shadow-lg shadow-black/10"
              onPointerDown={handleDragStart}
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-purple-500/20 via-violet-500/20 to-fuchsia-500/20 backdrop-blur-lg flex items-center justify-center text-xl font-semibold border border-white/20 shadow-lg shadow-purple-500/10">
                  🧮
                </div>
                <div>
                  <div className="text-sm font-bold tracking-wide bg-gradient-to-r from-slate-50 to-slate-300 bg-clip-text text-transparent">מחשבון מקצועי</div>
                  <div className="text-[11px] text-slate-400 font-medium">תומך במקלדת ובמסך מגע</div>
                </div>
              </div>
              <button
                onClick={() => setShowCalculator(false)}
                data-no-drag="true"
                type="button"
                className="text-slate-400 hover:text-white transition-all duration-200 rounded-xl p-2 hover:bg-gradient-to-br hover:from-rose-500/20 hover:to-red-500/20 hover:border hover:border-rose-400/30"
                title="סגור"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="flex flex-1 flex-col p-5 pb-6 gap-4 overflow-hidden">
              {renderModeSwitcher()}
              {(mode === 'basic' || mode === 'scientific') && (
                <>
                  {renderDisplay()}
                  {mode === 'scientific' && renderScientificPanel()}
                  {renderBasicPad()}
                </>
              )}
              {mode === 'converter' && (
                <div className="flex-1 overflow-y-auto">
                  {renderConverter()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Resize Handle - Bottom-left corner (like sticky notes) */}
        <div
          className="absolute bottom-0 left-0 w-4 h-4 cursor-se-resize z-[100] pointer-events-auto hover:bg-slate-700/50 transition-colors"
          onMouseDown={(e) => handleResizeMouseDown(e, 'both')}
          data-no-drag="true"
          style={{
            background: 'linear-gradient(135deg, transparent 50%, rgba(148, 163, 184, 0.4) 50%)',
          }}
          title="גרור לשינוי גודל"
        />
        
        {/* Resize Handle - Right edge (horizontal only) */}
        <div
          className="absolute top-1/2 right-0 -translate-y-1/2 w-3 h-12 cursor-ew-resize z-[100] pointer-events-auto hover:bg-slate-700/50 transition-colors"
          onMouseDown={(e) => handleResizeMouseDown(e, 'horizontal')}
          data-no-drag="true"
          style={{ borderRadius: '8px 0 0 8px' }}
          title="שנה רוחב"
        />
      </div>
    </div>
  );
};

export default Calculator;